import React, {Fragment, useState} from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import PhoneInput from "../../../bundles/checkout/components/phone_input";
import * as Yup from "yup";
import "yup-phone-lite";
import LoadingView from "../../../bundles/ui/LoadingView";
import { post } from '@rails/request.js'

const DemoAccount = ({email, smsConsentText, userId, plan}) => {
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = Yup.object().shape({
	first_name: Yup.string()
		.required("Please enter your first name.")
		.max(200, "Name is too long!"),
	last_name: Yup.string()
		.required("Please enter your last name.")
		.max(200, "Name is too long!"),
	email: Yup.string()
		.min(3, "Must be at least 3 characters")
		.email("Please enter a valid email address")
		.required("Please fill in this field."),
	password: Yup.string()
		.min(3, "Your password must contain at least 3 characters")
		.required("Please enter your password."),
	mobile_number: Yup.string()
		.phone("US", "Please enter a valid US phone number")
		.required("Please enter your mobile number."),
	zipcode: Yup.string()
		.required()
		.matches(/^[0-9]+$/, "Must be only digits")
		.min(5, 'Must be exactly 5 digits')
		.max(5, 'Must be exactly 5 digits'),
	member_terms_signed: Yup.bool()
		.oneOf([true], 'You must accept the terms of service.')
  });

  return (
	  <Fragment>
		{isLoading && <LoadingView text="Creating your account now! Hang tight, this will take a minute." />}
		<Formik
			initialValues={{
			  email: email,
			  password: '',
			  first_name: '',
			  last_name: '',
			  mobile_number: '',
			  sms_marketing_opted_in: false,
			  zipcode: '',
			  member_terms_signed: false
			}}
			validationSchema={validationSchema}
			onSubmit={async (values) => {
			  setIsLoading(true);
			  if (analytics) {
				let a = new TgAnalytics;

				analytics.track('Demo Account Form Completed', {
				  ...a.pageData,
				  ...values,
				});
			  }

			  const response = await post(`/api/v1/users/${userId}/demo_account`, {body: JSON.stringify(values)})
			  if (response.ok) {
				const data = await response.json;

				if (data.error) {
				  alert(data.error);
				} else if (data.redirect_url) {
				  window.location.href = data.redirect_url;
				}
			  } else {
				setIsLoading(false);
				alert('There was an error finalizing your account. Support has been notified and will reach out to you shortly.');
			  }
			}}
		>
		  {({values, isSubmitting}) => (
			  <div className="col-span-12">
				<div className="flex flex-col justify-center sm:px-6 lg:px-8 py-12">
				  <div className="sm:mx-auto sm:w-full sm:max-w-lg">
					<h3 className="text-center text-tgnavy">Finish Account Setup</h3>
				  </div>
				  <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-xl">
					<div className="flex flex-col bg-white px-4 shadow sm:rounded-lg sm:px-16 py-8 w-full">
					  <p className="px-2 mx-auto mb-8 text-md">
						Your free demo account is almost ready! Once you enter your information below, you’ll be taken to your Tinkergarten dashboard where you’ll enjoy full access to everything {`Tinkergarten ${plan}`} has to offer.
					  </p>
					  <Form>
						<div className="form-group">
						  <label className="text-xs pl-6 pt-4 ml-0 required">Email Address</label>
						  <div className="pl-6 font-bold mt-2">{values.email}</div>
						  <Field type="hidden" name="email"/>
						  <ErrorMessage name="email" component="p" className="error text-xs text-tgred ml-4"/>
						</div>

						<div className="form-group">
						  <label className="text-xs pl-6 pt-4 ml-0 required">First Name</label>
						  <Field type="text" name="first_name" className="form-control" autoFocus={true}/>
						  <ErrorMessage name="first_name" component="p" className="error text-xs text-tgred ml-4"/>
						</div>

						<div className="form-group">
						  <label className="text-xs pl-6 pt-4 ml-0 required">Last Name</label>
						  <Field type="text" name="last_name" className="form-control"/>
						  <ErrorMessage name="last_name" component="p" className="error text-xs text-tgred ml-4"/>
						</div>

						<div className="form-group">
						  <label className="text-xs pl-6 pt-4 ml-0 required">Password</label>
						  <Field type="password" name="password" className="form-control"/>
						  <ErrorMessage name="password" component="p" className="error text-xs text-tgred ml-4"/>
						</div>

						<div className="form-group">
						  <label className="text-xs pl-6 pt-4 ml-0 required">Zipcode</label>
						  <Field type="text" name="zipcode" className="form-control"/>
						  <ErrorMessage name="zipcode" component="p" className="error text-xs text-tgred ml-4"/>
						</div>

						<div className="form-group">
						  <label className="text-xs pl-6 pt-4 ml-0 required">Mobile Number</label>
						  <PhoneInput
							  name="mobile_number"
							  className="form-control mb-3 w-full"
							  tooltipText="For information and important updates about your class."
							  placeholder="e.g., (210) 555-1234"
							  id="mobile_number"
						  />
						  <ErrorMessage name="mobile_number" component="p" className="error text-xs text-tgred ml-4"/>
						</div>

						<section>
						  <div className="flex pt-3">
							<label className="checkbox-container">
							  <div className="text-sm">
								Text me about new classes &amp; special discounts
								<p className="mt-3 text-xs" dangerouslySetInnerHTML={{__html: smsConsentText}}/>
								<hr/>
							  </div>
							  <Field name="sms_marketing_opted_in" className="self-center" type="checkbox"/>
							  <span className="checkmark"/>
							</label>
						  </div>
						  <ErrorMessage name="sms_marketing_opted_in" component="p"
										className="error text-xs text-tgred ml-4"/>
						</section>

						<div>
						  <hr className="my-5"/>
						  <p className="flex items-start">
							<label className="checkbox-container">
							  <Field name="member_terms_signed" type="checkbox"/>
							  <span className="checkmark"></span>
							</label>
							<span className="pl-2">By clicking submit, I am agreeing to the <a href="/terms-of-service"
																							   target="_blank">Terms of Service</a><span
								className="text-tgred pl-1">*</span></span>
						  </p>
						  <ErrorMessage name="member_terms_signed" component="p"
										className="error text-xs text-tgred ml-4 pl-6"/>
						  <hr className="my-5"/>
						</div>

						<div className="form-group flex items-center">
						  <button type="submit" className="btn btn-solid my-3 cursor-pointer mx-auto"
								  disabled={isSubmitting}>
							Complete Account Setup
						  </button>
						</div>
					  </Form>
					</div>
				  </div>
				</div>
			  </div>
		  )}
		</Formik>
	  </Fragment>
  )
}
export default DemoAccount


// WEBPACK FOOTER //
// ./app/javascript/src/pages/demo_account/DemoAccount.jsx